import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useAuthStore } from '@/stores/auth-store';
import { toast } from 'sonner';
import { useEffect, useState } from "react";
import { handleApiError } from "@/lib/handle-api-error";
import { ApiError } from "@/services/api-client/base";
import { authService } from "@/services/api-client/auth";
import { PasswordInput } from "@/components/ui/password-input";
import { FormError } from '@/components/ui/form-error';

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "Senha é obrigatória"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const RESEND_COOLDOWN_SECONDS = 60;

export const LoginForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const login = useAuthStore((state) => state.login);
  // Cadastro feito mas link de ativação ainda não clicado: guarda a mensagem
  // do backend e o e-mail tentado, para oferecer o reenvio.
  const [pendingActivation, setPendingActivation] = useState<{
    email: string;
    message: string;
  } | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setPendingActivation(null);
    try {
      await login(data);
      toast.success("Login realizado com sucesso!");
      navigate('/dashboard');
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.body?.code === "account_not_activated"
      ) {
        setPendingActivation({ email: data.email, message: err.message });
        return;
      }
      if (
        err instanceof ApiError &&
        err.status === 401 &&
        !err.fieldErrors &&
        !err.nonFieldErrors
      ) {
        form.setError('root.serverError', { type: 'server', message: err.message });
      }
      handleApiError(err, { form });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendActivation = async () => {
    if (!pendingActivation) return;
    setIsResending(true);
    try {
      await authService.resendActivation(pendingActivation.email);
      toast.success(
        `Enviamos um novo link de confirmação para ${pendingActivation.email}.`
      );
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Entrar</CardTitle>
        <p className="text-sm">Digite seus dados de login para entrar</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleLogin)}>
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" placeholder="Digite seu email" {...register("email")} />
              <FormError message={errors.email?.message} />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="password">Senha</Label>
              <PasswordInput
                id="password"
                placeholder="Digite sua senha"
                {...register("password")}
              />
              <FormError message={errors.password?.message} />
            </div>
            <FormError message={errors.root?.serverError?.message} />
            {pendingActivation && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
                <p>{pendingActivation.message}</p>
                <p className="text-xs">Confira também a caixa de spam.</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleResendActivation}
                  disabled={isResending || resendCooldown > 0}
                >
                  {isResending
                    ? "Enviando..."
                    : resendCooldown > 0
                      ? `Reenviar novamente em ${resendCooldown}s`
                      : "Reenviar e-mail de confirmação"}
                </Button>
              </div>
            )}
            <Button className="w-full" type="submit" disabled={isLoading}>
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        <Link to="/forgot-password" className="text-sm text-center">Esqueci minha senha</Link>
        <hr className="w-full border-t border-gray-100 mt-2" />
        <Link to="/signup" className="text-sm text-center">Ainda não tem um cadastro? <b>Crie sua conta</b>!</Link>
      </CardFooter>
    </Card>
  );
};